import React, { useCallback, useState } from "react";
import { useSocket } from "../../game/socketContext.tsx";
import type { Card, GameState, Player } from "./gameboardTypes.tsx";
import './game.css';
import Hand from "./zones/hand/Hand.tsx";
import useWindowDimensions from "../../util/windowUtils.ts";
import StaticCard from "./staticCard.tsx";
import { PlayingCard } from "./cardComponent.tsx";
import { BASE_PLAYER_HEIGHT, PlayerInfoCard } from "./zones/status/PlayerStatus.tsx";
import { UIButton } from "./zones/status/UIButton.tsx";
import Battlefield from "./zones/battlefield/battlefield.tsx";
import Clickable from "./Clickable.tsx";
import { EffectStack } from "./zones/battlefield/abilities.tsx";
import { SCHOOL_COLORS, type Effect, type Target } from "../../util/cardEffects/effectTypes.ts";
import { NumberSelector } from "./zones/status/NumberSelector.tsx";
import { createText } from "../../util/cardEffects/cardText.ts";
import CardListSelector from "./zones/deck/CardListSelector.tsx";
import { cardHasEffectType } from "../../util/cardEffects/effectProcessing.tsx";
import TurnTimer, { useTurnTimer } from "./TurnTimer.tsx";
import { GameOverScreen } from "./WinLoseScreen.tsx";
import { useNavigate } from "react-router";
import useCardDimensions, { useCardScale, usePlayerCardDimensions, useUIButtonDimensions } from "../../util/cardUtils.ts";

export default function GameBoard() {
    const [gameState, setGameState] = useState<GameState | null>(null);

    const { height } = useWindowDimensions();

    const [selectData, setSelectMode] = useState<null | { target: Target, potentialPublicIds: string[], deck?: [] }>(null);
    const [numberData, setNumberData] = useState<null | { min: number, max: number, title: Effect }>(null);
    const [responseId, setResponseId] = useState<null | string>(null);

    const { startTimer, cancelTimer, timerProps } = useTurnTimer(1000 * 60 * 0.5); // 30s
    const { startTimer: startTurnTimer, timerProps: turnTimerProps } = useTurnTimer(1000 * 60 * 1); // 1m x 1

    const nav = useNavigate();

    const resetResponse = useCallback(() => {
        setResponseId(null);
        setSelectMode(null);
        setNumberData(null);
    }, []);

    const onmessage = useCallback(
        (msg: MessageEvent) => {
            const parsed = JSON.parse(msg.data);
            if (parsed.action === "gameState") {

                setGameState(parsed.data);
                startTurnTimer(parsed.endOfTurnTimestamp);
                if (parsed.maxTriggerTimestamp > Date.now()) {
                    startTimer(parsed.maxTriggerTimestamp);
                } else {
                    cancelTimer();
                }
                if (!parsed.dontClearResponses) resetResponse();
            }
            if (parsed.action === "target") {
                setResponseId(parsed.responseId);
                setSelectMode(parsed.data);
                startTimer(parsed.endingTimestamp);
            }
            if (parsed.action === "number") {
                setResponseId(parsed.responseId);
                setNumberData(parsed.data);
                startTimer(parsed.endingTimestamp);
            }
            if (parsed.action === "return") {
                nav(parsed.to || '/games');
            }
        },
        [cancelTimer, nav, resetResponse, startTimer, startTurnTimer]
    );

    const websocket = useSocket(onmessage, useCallback((ws) => {
        ws.send(JSON.stringify({ action: "joinPlay" }));
        console.log("true");
    }, []));

    if (!gameState) return <div>Loading game...</div>;

    const { player, opponents } = gameState;
    const classCardScale = 0.9;
    const turnPlayer = (player.turn ? player : opponents[0]);
    const nonTurnPlayer = (!player.turn ? player : opponents[0]);
    const currentDisplayedStack = turnPlayer?.stack?.length > 0 ? turnPlayer?.stack : nonTurnPlayer?.stack;


    return (<div style={{
        position: "relative",
        backgroundColor: "#555555",
        width: "100%",
        height: `${height}px`,
        overflow: "hidden",
        zIndex: 0,
    }}>
        <ClassCards player={player} websocket={websocket} height={height} classCardScale={classCardScale} opponents={opponents} />
        <Timers player={player} opponents={opponents} timerProps={timerProps} turnTimerProps={turnTimerProps} />
        <PlayerBattlefields cards={selectData?.potentialPublicIds} responseId={responseId} ws={websocket} player={player} opponents={opponents} classCardScale={classCardScale} />
        <UIElements player={player} stack={currentDisplayedStack} websocket={websocket} gameState={gameState} />
        <PlayerDataDisplay responseId={responseId} websocket={websocket} player={player} opponents={opponents} selectData={selectData} resetResponse={resetResponse} classCardScale={classCardScale} />
        <Hands websocket={websocket} player={player} opponents={opponents} />
        <Stack classCardScale={classCardScale} currentDisplayedStack={currentDisplayedStack} />
        <Selectors websocket={websocket} resetResponse={resetResponse} responseId={responseId} selectData={selectData} numberData={numberData} />

    </div>);
}

function Selectors({ numberData, websocket, resetResponse, responseId, selectData }: {
    numberData: { min: number; max: number; title: Effect; } | null,
    selectData: null | { target: Target, potentialPublicIds: string[], deck?: [] },
    resetResponse: Function,
    responseId: null | string,
    websocket: WebSocket | undefined
}) {
    return <>
        <NumberSelector
            min={numberData?.min}
            max={numberData?.max}
            visible={numberData !== null}
            onSubmit={(value) => {
                websocket?.send(JSON.stringify({ action: "response", responseId: responseId, data: { number: value } }))
                resetResponse();
            }}
            title={numberData?.title ? `${createText(numberData.title)} | Set X to:` : "Pay X as: "}
        />

        {
            selectData && selectData.deck && selectData.target.type === "card" && selectData.target.zone === "deck" ?
                <CardListSelector
                    cards={selectData.deck}
                    onSubmit={(cardId) => {
                        websocket?.send(JSON.stringify({ action: "response", responseId: responseId, data: { entityId: cardId } }))
                        resetResponse();
                    }}
                /> : null
        }
    </>
}

function Stack({ classCardScale, currentDisplayedStack }: { classCardScale: number, currentDisplayedStack: any }) {
    const { cardWidth } = useCardDimensions()
    const scale = useCardScale();

    const xPos = classCardScale * cardWidth;
    const yPos = (BASE_PLAYER_HEIGHT * scale);

    return <>
        <EffectStack
            x={xPos}
            y={yPos}
            effects={currentDisplayedStack}
        />
    </>
}

function Hands({ player, opponents, websocket }: { player: Player, opponents: Player[], websocket: WebSocket | undefined }) {
    return <>
        <Hand
            cards={opponents[0]?.hand}
        />
        <Hand
            cards={player.hand}
            playerFocus={player.pool}
            playCard={(cardId: string) => {
                websocket?.send(JSON.stringify({ action: "playCard", publicCardId: cardId }));
            }}
        />
    </>
}

function PlayerDataDisplay({ player, opponents, websocket, resetResponse, responseId, selectData, classCardScale }: { selectData: null | { target: Target, potentialPublicIds: string[], deck?: [] }, classCardScale: number, player: Player, resetResponse: Function, responseId: null | string, opponents: Player[], websocket: WebSocket | undefined }) {
    const { cardWidth } = useCardDimensions(.9);
    const { cardHeight } = usePlayerCardDimensions(classCardScale);
    const { height } = useWindowDimensions();
    const scale = useCardScale();

    return <>
        {/* Honestly not sure why the (intended) offset is off by scale*10, but this fixes it. i think it might be the box-shadow? it doesn't happen on the class cards though */}
        <StaticCard
            Y={height - cardHeight - (scale * 10)}
            X={cardWidth}
        >
            <Clickable
                clickable={selectData?.target?.type === "player" && (!selectData?.target.controller || selectData?.target.controller === "opponent")}
                onClick={() => {
                    websocket?.send(JSON.stringify({ action: "response", responseId: responseId, data: { entityId: player.userId } }))
                    resetResponse();
                }}
            >
                <PlayerInfoCard
                    name={player.username}
                    health={player.health}
                    school={player.class?.school ?? "Manifest"}
                    poolFocus={player.pool.count}
                    poolTempFocus={player.pool.temp_count}
                    nextTurnFocus={player.clock.count}
                    nextTurnTempFocus={player.clock.temp_count}
                    ritualIntensity={player.ritualIntensity}
                    deckCount={player.deck}
                    discardCount={player.discard.length}
                    highlight={player.turn}
                />
            </Clickable>
        </StaticCard>
        <StaticCard
            Y={0}
            X={cardWidth}
        >
            <Clickable
                clickable={selectData?.target?.type === "player" && (!selectData?.target?.controller || selectData?.target?.controller === "self")}
                onClick={() => {
                    websocket?.send(JSON.stringify({ action: "response", responseId: responseId, data: { entityId: opponents[0]?.userId } }))
                    resetResponse();
                }}
            >
                <PlayerInfoCard
                    name={opponents[0]?.username}
                    health={opponents[0]?.health}
                    school={opponents[0]?.class?.school ?? "Manifest"}
                    poolFocus={opponents[0]?.pool.count}
                    poolTempFocus={opponents[0]?.pool.temp_count}
                    nextTurnFocus={opponents[0]?.clock.count}
                    nextTurnTempFocus={opponents[0]?.clock.temp_count}
                    ritualIntensity={opponents[0]?.ritualIntensity}
                    deckCount={opponents[0]?.deck}
                    discardCount={opponents[0]?.discard.length}
                    highlight={opponents[0]?.turn}
                />
            </Clickable>
        </StaticCard>
    </>
}

function UIElements({ player, websocket, stack, gameState }: { player: Player, websocket: WebSocket | undefined, stack: any[], gameState: GameState }) {
    const { height, width } = useWindowDimensions();
    const { buttonHeight, buttonWidth } = useUIButtonDimensions();
    return <>
        {player.turn && stack?.length === 0 ?
            <UIButton
                x={width - buttonWidth}
                y={height - buttonHeight}
                onClick={() => {
                    websocket?.send(JSON.stringify({ action: "passTurn" }))
                }}
                school={player.class?.school ?? "Manifest"}
                scale={1}
            >
                <p>Pass Turn</p>
            </UIButton> : ""
        }
        <UIButton
            x={width - (buttonWidth / 2)}
            y={0}
            onClick={() => {
                websocket?.send(JSON.stringify({ action: "concede" }))
            }}
            school={player.class?.school ?? "Manifest"}
            scale={0.5}
        >
            <p>Concede</p>
        </UIButton>
        <GameOverScreen
            won={!!gameState.winners.find(p => player.userId === Number(p))}
            visible={!!gameState.winners.find(p => player.userId === Number(p)) || !!gameState.losers.find(p => player.userId === Number(p))}
        />
    </>
}

function PlayerBattlefields({ player, opponents, classCardScale, ws, responseId, cards }: { cards?: string[], responseId: string | null, ws?: WebSocket, classCardScale: number, player: Player, opponents: Player[] }) {
    const battlefieldCardScale = .615;
    const { height, width } = useWindowDimensions();
    const { cardWidth, cardHeight } = useCardDimensions()
    const startX = classCardScale * cardWidth;

    return <>
        <Battlefield
            battlefieldCardScale={battlefieldCardScale}
            cards={opponents[0]?.board}
            x={startX}
            y={(classCardScale * cardHeight) - (cardHeight * battlefieldCardScale)}
            maxWidth={width - startX}
            clickable={(cardId) => {
                return cards?.includes(cardId)
            }}
            onCardClick={(cardId) => {
                ws &&
                    ws.send(JSON.stringify({ action: "response", responseId: responseId, data: { entityId: cardId } }));
            }}
        />
        <Battlefield
            battlefieldCardScale={battlefieldCardScale}
            cards={player.board}
            x={startX}
            y={height - (classCardScale * cardHeight)}
            maxWidth={width - startX}
            clickable={(cardId) => {
                return cards?.includes(cardId)
            }}
            onCardClick={(cardId) => {
                ws &&
                    ws.send(JSON.stringify({ action: "response", responseId: responseId, data: { entityId: cardId } }));
            }}
        />
    </>;
}

function Timers({ player, opponents, timerProps, turnTimerProps }: { player: Player, opponents: Player[], timerProps: any, turnTimerProps: any }) {
    const { height } = useWindowDimensions();

    return <>
        <TurnTimer
            showNumber={true}
            showTimeoutCount={player.turn ? player.timeoutCount : opponents[0]?.timeoutCount}
            topValue={(height / 2) - 3}
            color={player.turn ? (player.class ? SCHOOL_COLORS[player.class.school!] : "#FFF") : (opponents[0]?.class ? SCHOOL_COLORS[opponents[0].class.school!] : "#FFF")}
            {...turnTimerProps}
        />
        <TurnTimer
            showNumber={false}
            showTimeoutCount={player.turn ? player.timeoutCount : opponents[0]?.timeoutCount}
            topValue={(height / 2) + 3}
            color={player.turn ? (player.class ? SCHOOL_COLORS[player.class.school!] : "#FFF") : (opponents[0]?.class ? SCHOOL_COLORS[opponents[0].class.school!] : "#FFF")}
            {...timerProps}
        />
    </>
}

function ClassCards({ player, websocket, height, classCardScale, opponents }: { player: Player, websocket: WebSocket | undefined, height: number, classCardScale: number, opponents: Player[] }) {
    const { cardHeight } = useCardDimensions(.9);

    let ret = [];

    if (player.class) {
        ret.push(<Clickable clickable={player.turn
            && player.class.focus.count + player.class.focus.temp_count <= 0
            && !!player.class.energizeAmount
            && player.pool.count + player.pool.temp_count >= player.class.energizeAmount} onClick={() => {
                websocket?.send(JSON.stringify({ action: "energizeClass" }));
            }}>
            <StaticCard
                Y={height - (cardHeight)}
            >
                <PlayingCard
                    name={player.class.name}
                    cost={player.class.cost}
                    focus={player.class.focus.count}
                    tempFocus={player.class.focus.temp_count}
                    energizeAmount={player.class.energizeAmount}
                    school={player.class.school ?? "Manifest"}
                    ritual={cardHasEffectType("ritual", player.class)}
                    classCard={true}
                    castThreshold={player.class.castThreshold}
                    cast={createText(player.class.castAbility)}
                    play={createText(player.class.playAbility)}
                    sustain={createText(player.class.sustainAbility)}
                    passive={createText(player.class.passiveAbility)}
                    scale={classCardScale}
                    forceNoHightlight={!player.turn} />
            </StaticCard>
        </Clickable>);
    }

    if (opponents[0]?.class) {
        ret.push(
            <StaticCard
                Y={0}
            >
                <PlayingCard
                    name={opponents[0]?.class.name}
                    cost={opponents[0]?.class.cost}
                    focus={opponents[0]?.class.focus.count}
                    tempFocus={opponents[0]?.class.focus.temp_count}
                    energizeAmount={opponents[0]?.class.energizeAmount}
                    school={opponents[0]?.class.school ?? "Manifest"}
                    ritual={cardHasEffectType("ritual", opponents[0]?.class)}
                    classCard={true}
                    castThreshold={opponents[0]?.class.castThreshold}
                    cast={createText(opponents[0]?.class.castAbility)}
                    play={createText(opponents[0]?.class.playAbility)}
                    sustain={createText(opponents[0]?.class.sustainAbility)}
                    passive={createText(opponents[0]?.class.passiveAbility)}
                    scale={classCardScale}
                    forceNoHightlight={!opponents[0]?.turn} />
            </StaticCard>);
    }
    return ret;
}

